/* eslint-disable class-methods-use-this */
/* eslint-disable no-param-reassign */
import {
  ANY_XML_FIELD_IDENTIFIER_REGEX,
  IS_STRING,
} from 'apps/web_forms/expressions/regularExpressions';
import { defaultExpressionResolver } from './expressionResolvers';
import {
  inputTagNames,
  tagNameToFieldType,
  groupTagNames,
  expressionFunctionMappings,
  attributeMappings,
  tagNameToAttributes,
  formAttributeMappings,
  childAttributeMappings,
  attributeIgnore,
  dispatchTypes,
} from './config';

class XMLFormParser {
  constructor(xmlNode, fullXmlVersion = true) {
    this.xmlNode = xmlNode;

    if (fullXmlVersion) {
      this.inputNodeList = Array.from(
        xmlNode.getElementsByTagName('inputs')[0].children
      );

      this.inputDataNodeList = Array.from(
        xmlNode.getElementsByTagName('h:body')[0].children
      );

      this.inputDataBindings = Array.from(xmlNode.getElementsByTagName('bind'));

      this.formAttributes = {};
      this.getFormAttributes();
      this.isDispatchOrSubmission =
        !!this.formAttributes.scheduledAt ||
        window.location.href.includes('origin=dispatch')||
        window.location.href.includes('origin=submission');

      this.getInputsFromNodeList();
      this.generateInputMap();
      this.bindInputData();
      this.bindInitialAnswers();

      const formTitle = xmlNode.getElementsByTagName('h:title')[0].innerText;

      // this.formAttributes should include stuff like namespace, dependentResources, and dispatches info
      this.result = {
        title: formTitle,
        inputList: this.inputList,
        formType: this.getFormType(),
        type: 'root',
        getInput: this.getInput.bind(this),
        ...this.formAttributes,
      };

      if (this.isDispatchOrSubmission) {
        this.result.dispatchInitialAnswers = this.initialAnswerTree;
        this.result.dispatchInputList = this.getDispatchInputList();
      }
    } else {
      this.inputNodeList = Array.from(
        xmlNode.getElementsByTagName('inputs')[0].children
      );

      this.getInputsFromNodeList();
      this.generateInputMap(true);
    }
  }

  getDispatchInputList() {
    function iterateElements(inputsList, mutatedInputList = []){
      inputsList.map((input) => {
        if (input.children) {
          if(!input.type){
            input.type = 'group';
          }
          mutatedInputList.push(input);
          inputsList = iterateElements(input.children, mutatedInputList);
        } else {
          mutatedInputList.push(input);
        }
      }, []);
      return mutatedInputList;
    };

    const inputsList = JSON.parse(JSON.stringify(this.inputList));
    /** Reverse inputs because inputsList last objects are filled with necessary attributes that are needed to create indexes for
     * identeifiers that are going to be added to dispatchAnswers in redux
     */
    const inputsListReversed = this.updateObjects(inputsList.reverse())
    return iterateElements(inputsListReversed)
  }

  getFormType() {
    const { address, location, geoFenceRadius } = this.formAttributes;
    const { GEO_FENCED, GEO_LOCATED, REGULAR } = dispatchTypes;
    if (
      address !== undefined &&
      location !== undefined &&
      geoFenceRadius !== undefined
    ) {
      return GEO_FENCED;
    }
    if (address !== undefined && location !== undefined) {
      return GEO_LOCATED;
    }
    return REGULAR;
  }

  getFormAttributes() {
    const mapAttribute = this.mapAttribute.bind(this);

    const instance = this.xmlNode.getElementsByTagName('instance')[0];

    Array.from(instance.attributes).forEach((attribute) => {
      const mappedAttributes = mapAttribute({
        attribute: {
          name: attribute.name,
          value: attribute.value,
        },
        customAttributeMappings: formAttributeMappings,
      });

      if (!mappedAttributes) return;

      mappedAttributes.forEach(({ name, value }) => {
        this.formAttributes[name] = value;
      });
    });
  }

  generateInputMap(storeGroupsSeparatedly = false) {
    const inputMap = new Map();

    function iterateList(list, parentIdentifier = '') {
      list.forEach((input) => {
        const currentAbsouluteIdentifier = `${parentIdentifier}${input.identifier}`;
       
        if (
          (storeGroupsSeparatedly &&
            inputMap.has(currentAbsouluteIdentifier)) ||
          (storeGroupsSeparatedly &&
            Object.prototype.hasOwnProperty.call(input, 'children'))
        ) {
          const currentValue = inputMap.get(currentAbsouluteIdentifier);
          const inputList = [];

          if (Array.isArray(currentValue)) {
            inputList.push(...currentValue);
          } else if (currentValue !== undefined) {
            inputList.push(currentValue);
          }

          inputList.push(input);
          
          inputMap.set(currentAbsouluteIdentifier, inputList);
        } else {
          inputMap.set(currentAbsouluteIdentifier, input);
        }

        input.identifier = currentAbsouluteIdentifier;

        if (input.children) {
          iterateList(input.children, `${currentAbsouluteIdentifier}/`);
        }
      });
    }

    iterateList(this.inputList);
    this.inputMap = inputMap;
  }

  getInput(absoluteIdentifier) {
    return this.inputMap?.get(absoluteIdentifier);
  }

  getInputsFromNodeList() {
    function iterateList(list) {
      const inputList = [];

      list.forEach((inputNode) => {
        const currentObj = {};
        currentObj.identifier = inputNode.tagName;

        const imageIdentifier = inputNode.getAttribute('imageIdentifier');
        if (imageIdentifier) {
          currentObj.imageIdentifier = imageIdentifier;
        }

        if (inputNode.childElementCount > 0) {
          currentObj.children = iterateList([...inputNode.children]);
        } else if (inputNode.childElementCount === 0 && inputNode.textContent) {
          currentObj.initialAnswer = inputNode.textContent;
        }
        inputList.push(currentObj);
      });

      return inputList;
    }

    this.inputList = iterateList(this.inputNodeList);
  }

  bindInputData() {
    const getInput = this.getInput.bind(this);
    const mapAttribute = this.mapAttribute.bind(this);

    const notHiddenInputMap = new Map();

    function iterateList(list, parentIdentifier = '') {
      list.forEach((inputDataNode) => {
        if (!inputTagNames.includes(inputDataNode.tagName)) return;

        const absoluteFieldIdentifier = `${parentIdentifier}${
          inputDataNode.getAttribute('ref') ||
          inputDataNode.getAttribute('nodeset')
        }`;

        const currentInputRef = getInput(absoluteFieldIdentifier);

        notHiddenInputMap.set(absoluteFieldIdentifier);

        // Bind input attributes
        Array.from(inputDataNode.attributes).forEach((attribute) => {
          const mappedAttributes = mapAttribute({
            attribute: {
              name: attribute.name,
              value: attribute.value,
            },
            containerFieldId: absoluteFieldIdentifier,
          });
          
          if (!mappedAttributes) return;

          mappedAttributes.forEach(({ name, value }) => {
            currentInputRef[name] = value;
          });
        });

        // Infer input attributes from tag name
        if (tagNameToAttributes[inputDataNode.tagName]) {
          Object.entries(tagNameToAttributes[inputDataNode.tagName]).forEach(
            ([key, value]) => {
              currentInputRef[key] = value;
            }
          );
        }

        // Bind input attributes from child list
        Array.from(inputDataNode.children).forEach((childDataNode) => {
          const attributeDef = childAttributeMappings[childDataNode.tagName];

          if (attributeDef) {
            const { attributeName } = attributeDef;

            if (attributeDef.children) {
              currentInputRef[attributeName] =
                currentInputRef[attributeName] || [];

              const newArrayItem = {};

              Array.from(childDataNode.children).forEach((childOfChild) => {
                const childAttributeDef =
                  attributeDef.children[childOfChild.tagName];

                if (childAttributeDef) {
                  newArrayItem[childAttributeDef.attributeName] =
                    childOfChild.textContent;
                }
              });

              currentInputRef[attributeName].push(newArrayItem);
            } else {
              currentInputRef[attributeName] = childDataNode.textContent;
            }
          }
        });

        // If this is a group, look for inputs inside children
        if (groupTagNames.includes(inputDataNode.tagName)) {
          iterateList(
            Array.from(inputDataNode.children),
            `${absoluteFieldIdentifier}/`
          );
      
          // Bind field type, since "group" type should be inferred from the tag name
          currentInputRef.type = 'group';
        }

        // Try to infer other input types from the tag name
        const inferredType = tagNameToFieldType[inputDataNode.tagName];
        if (inferredType) currentInputRef.type = inferredType;
      });
    }

    iterateList(this.inputDataNodeList);
    this.readXMLBindElements();
    this.fillTextTypes();

    // Search for hidden calculated inputs and give them the "hidden" attribute
    Array.from(this.inputMap).forEach(([id, inputRef]) => {
      if (!notHiddenInputMap.has(id)) {
        inputRef.hidden = true;
      }
    });
  }

  bindInitialAnswers() {
    const { isDispatchOrSubmission } = this;
    const getInput = this.getInput.bind(this);
    let levelOfFirstGroup = 0;

    function iterateList(
      list,
      parentIdentifier = '',
      bindInitialAnswer = true,
      isPartOfSubform = false,
      isPartOfGroup = false,
      level = 1,
    ) {
      const initialAnswerTree = {};

      list.forEach((inputNode) => {
        const absoluteFieldIdentifier = `${parentIdentifier}${inputNode.tagName}`;

        const currentInputRef = getInput(absoluteFieldIdentifier);

        const isGroup = currentInputRef.type === 'group';
        const isRepeatGroup = isGroup && currentInputRef.maxOccurs !== 1;
        
        if(isRepeatGroup && levelOfFirstGroup === 0){
          levelOfFirstGroup = level
        }

        if (isGroup) {
          const childInitialAnswerTree = iterateList(
            [...inputNode.children],
            `${absoluteFieldIdentifier}/`,
            !(isDispatchOrSubmission && isRepeatGroup),
            currentInputRef.maxOccurs === 1,
            isRepeatGroup,
            level + 1
          );
          
          if(isPartOfSubform){
            currentInputRef.isPartOfSubform = true
            if(levelOfFirstGroup !== 0 && levelOfFirstGroup !== level){
              currentInputRef.isPartOfGroup = true
            }
          }

          if(isPartOfGroup){
            currentInputRef.isPartOfGroup = true
          }

          if (!initialAnswerTree[inputNode.tagName]) {
            initialAnswerTree[inputNode.tagName] = [];
          }

          initialAnswerTree[inputNode.tagName].push(childInitialAnswerTree);
        } else if (inputNode.textContent) {
          initialAnswerTree[inputNode.tagName] = inputNode.textContent;

          if (bindInitialAnswer) {
            currentInputRef.initialAnswer = inputNode.textContent;
          }
        } else {
          if(isPartOfSubform){
            currentInputRef.isPartOfSubform = true
            if(levelOfFirstGroup !== 0 && levelOfFirstGroup !== level){
              currentInputRef.isPartOfGroup = true
            }
          }

          if(isPartOfGroup){
            currentInputRef.isPartOfGroup = true
          }
        }
      });

      return initialAnswerTree;
    }

    const initialAnswerTree = iterateList(this.inputNodeList);

    this.initialAnswerTree = initialAnswerTree;
  }

  /**
   * Bind input attributes from XML "bind" elements
   */
  readXMLBindElements() {
    const getInput = this.getInput.bind(this);
    const mapAttribute = this.mapAttribute.bind(this);

    this.inputDataBindings.forEach((inputBinding) => {
      const absoluteFieldIdentifier = inputBinding.getAttribute('nodeset');
      const currentInputRef = getInput(absoluteFieldIdentifier);

      Array.from(inputBinding.attributes).forEach((attribute) => {
        const mappedAttributes = mapAttribute({
          attribute: {
            name: attribute.name,
            value: attribute.value,
          },
          containerFieldId: absoluteFieldIdentifier,
        });

        if (!mappedAttributes) return;

        mappedAttributes.forEach(({ name, value }) => {
          currentInputRef[name] = currentInputRef[name] ?? value;
        });
      });

      // Special case: Set "calculated" as the type of all inputs which have a calculated expression
      if (currentInputRef.calculate_expr) currentInputRef.type = 'calculated';
    });
  }

  /**
   * Search for inputs without a "type" attribute, then fill them with "text"
   * since it's the default input type.
   */
  fillTextTypes() {
    this.inputMap.forEach((input) => {
      input.type = input.type || 'text';
    });
  }

  transformExpression(expression, containerFieldId) {
    const getInput = this.getInput.bind(this);
    // Strings should be removed to use regular expressions correctly
    let newExpression = expression.replace(/'[^']*'/g, `[string]`);
    // Transform syntax of functions
    expressionFunctionMappings.forEach(
      ({ regex, newFunctionName, resolverFunction }) => {
        if (newExpression.match(regex)) {
          if (resolverFunction) {
            if (!newExpression.match(IS_STRING)) {
              newExpression =
                resolverFunction(regex, newExpression) || newExpression;
            } else {
              newExpression = defaultExpressionResolver(
                regex,
                newExpression,
                newFunctionName
              );
            }
          } else if (newFunctionName) {
            newExpression = defaultExpressionResolver(
              regex,
              newExpression,
              newFunctionName
            );
          }
        }
      }
    );

    // Transform syntax of field identifiers
    newExpression = newExpression.replace(
      ANY_XML_FIELD_IDENTIFIER_REGEX,
      (fullMatch, exactMatch) => {
        let newFieldIdentifier = exactMatch;
        if (newFieldIdentifier.startsWith('../')) {
          newFieldIdentifier = newFieldIdentifier.slice(3);
        }

        const containerType = getInput(containerFieldId).type_derived;

        const absoluteIdentifier = normalizeFormulaFieldIdentifier(
          newFieldIdentifier,
          containerFieldId,
          containerType
        );
        const thisFieldExists = !!getInput(absoluteIdentifier);

        const leftString = fullMatch.slice(0, -exactMatch.length);

        return `${leftString}${
          thisFieldExists ? absoluteIdentifier : exactMatch
        }`;
      }
    );

    /* Modifying string that has square brackets 
    example Group[INDEX_OF(Group/Free_Text_Question) - 1]Group/Free_Text_Question => Group[INDEX_OF(Group/Free_Text_Question) - 1]/Free_Text_Question */
    if (newExpression.match(/(]\/(.*\/))/)) {
      newExpression = newExpression.replace(/(]\/(.*\/))/, ']/');
    }

    // Merge strings again
   
    const exprStrings = expression.match(/'[^']*'/g);
    if (exprStrings) {
      let i = -1;
      newExpression = newExpression.replace(/\[string\]/g, () => {
        i += 1;

        // Replace single quotes with double quotes
        const exprString = `"${exprStrings[i].slice(1, -1)}"`;

        return exprString;
      });
    }

    return newExpression;
  }

  mapAttribute({
    attribute: { name, value },
    containerFieldId,
    customAttributeMappings = attributeMappings,
  }) {
    const transformExpression = this.transformExpression.bind(this);
    const getInput = this.getInput.bind(this);

    if (attributeIgnore[name]) return null;
    const currentMapping = customAttributeMappings[name];
    let newName;
    let newValue;

    if (currentMapping) {
      newName = currentMapping.newName || name;
      newValue = currentMapping.valueMappings?.[value] || value;

      if (currentMapping.valueMappingFunction) {
        newValue = currentMapping.valueMappingFunction({
          value,
          inputRef: getInput(containerFieldId),
          transformExpression: (expr) =>
            transformExpression(expr, containerFieldId),
        });
      }

      if (currentMapping.parseInt) {
        newValue = parseInt(newValue, 10);
      }
      if (currentMapping.parseBooleans) {
        if (newValue === 'true') newValue = true;
        if (newValue === 'false') newValue = false;
      }

      if (
        currentMapping.isExpression &&
        !currentMapping.valueMappings?.[value]
      ) {
        newValue = transformExpression(value, containerFieldId);
      }
    }

    const outputList = [{ name: newName, value: newValue }];

    if (currentMapping?.cloneAttributeTo) {
      currentMapping.cloneAttributeTo.forEach((cloneName) =>
        outputList.push({ name: cloneName, value: newValue })
      );
    }

    return outputList;
  }

  /**This function is created to update inputList objects since there is a difference between the objects in the array
   * the difference is caused by the function getInput that takes the reference of the last object only
   * When this function finishes executing, all objects that have the same identifier will have the same properties, except for initialAnswer and children
   */
  updateObjects(objects) {
    const identifierMap = new Map();
  
    // Traverse the objects and update the identifierMap
    function traverse(obj) {
     
      if (obj.identifier in identifierMap) {
        // Merge attributes except "initialAnswer"
        const existing = identifierMap[obj.identifier];
        for (const key in existing) {
          if (key !== "initialAnswer" && key !== "children") {
            obj[key] = existing[key];
          }
        }
      } else {
        identifierMap[obj.identifier] = obj;
      }

      // Recursively process children if present
      if (obj.children && obj.children.length > 0) {
        for (let i = obj.children.length - 1; i >= 0; i--) {
          const currentObject = obj.children[i];
          traverse(currentObject)
        }
      }

    }
  
    objects.forEach(obj => traverse(obj));
    return objects.reverse();
  }
}

// Util functions...

/**
 * Returns an array with all XML field identifiers found in the given expression
 * @param {string} expr
 * @returns Array
 */
export const getXMLExpressionFields = (expr) => {
  const matches = expr
    // Take field names
    .matchAll(ANY_XML_FIELD_IDENTIFIER_REGEX);

  // Take the second captured group of each match
  return [...matches].map((groups) => groups[1]) || [];
};

/**
 * Convert relative field identifiers to absoulte paths
 * The "formulaField" param represents the identifier of the field which is in the expression
 * The "identifier" param represents the identifier of the field which is containing the expression
 *
 * Example:
 * Input:  { identifier: "Subform_A/Subform_B/Calculated_Input", formulaField: "../Text_Input" }
 * Output: "Subform_A/Text_Input"
 *
 * @param {String} formulaField
 * @param {String} identifier
 */
const normalizeFormulaFieldIdentifier = (formulaField, identifier) => {
  const splittedFieldId = formulaField.split('../');

  const [fieldIdentifier] = splittedFieldId.slice(-1);
  const levelUpCount = splittedFieldId.length - 1;

  const groups = identifier.split('/');
  const prefix = groups.slice(0, -(levelUpCount + 1)).join('/');
  return prefix ? `${prefix}/${fieldIdentifier}` : fieldIdentifier;
};

export default XMLFormParser;
